/**
 * Contracts for module "seo-loop" (owner: seo-loop). SEO/GEO loop foundation: per-tenant settings, approval contacts,
 * treatments and confirmed facts (GROWTH_DB, growth migration 0600). Flag seo-loop.enabled (default off).
 * Plan: docs/plans/seo-geo-approvals-gap-analysis.md.
 *
 * Owner decisions (3 Nov 2026): one client approver (approval_policy 'single' only; no medical-review path, so no
 * screen or output ever claims medical review); SERP from Serper.dev with a platform cap of USD 50/month and a
 * per-tenant credit sub-cap (serpMonthlyCreditCap).
 */
import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, TenantIdSchema } from './common';

export const SEO_LOOP_FLAG = 'seo-loop.enabled' as const;

export const ApprovalPolicySchema = z.enum(['single']);
export type ApprovalPolicy = z.infer<typeof ApprovalPolicySchema>;

/** IANA zone name such as Asia/Dubai (checked against Intl at the edge where the zone is used). */
export const TimeZoneSchema = z.string().min(3).max(64).regex(/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+$|^UTC$/);
export const HttpsUrlSchema = z.string().max(2048).url().refine((u) => u.startsWith('https://'), 'must be https');
export const E164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/, 'E.164 phone number');
/** 0 or 1 as stored in SQLite. */
const Bit = z.union([z.literal(0), z.literal(1)]);

export const SeoTenantSettingsRowSchema = z.object({
  tenant_id: TenantIdSchema,
  timezone: TimeZoneSchema,
  approval_policy: ApprovalPolicySchema,
  area_keyword: z.string().min(1).max(120).nullable(),
  wp_base_url: HttpsUrlSchema.nullable(),
  serp_monthly_credit_cap: z.number().int().min(0).nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
}).strict();
export type SeoTenantSettingsRow = z.infer<typeof SeoTenantSettingsRowSchema>;

export const SeoContactRoleSchema = z.enum(['approver', 'viewer']);
export const SeoContactRowSchema = z.object({
  id: IdSchema,
  tenant_id: TenantIdSchema,
  role: SeoContactRoleSchema,
  user_id: IdSchema.nullable(),
  crm_contact_id: IdSchema.nullable(),
  display_name: z.string().trim().min(1).max(120),
  email: z.string().email().max(254).nullable(),
  whatsapp_e164: E164Schema.nullable(),
  status: z.enum(['active', 'removed']),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
}).strict();
export type SeoContactRow = z.infer<typeof SeoContactRowSchema>;

export const SeoTreatmentRowSchema = z.object({
  id: IdSchema,
  tenant_id: TenantIdSchema,
  name: z.string().trim().min(1).max(120),
  /** JSON array of alternative names, as stored */
  aliases_json: z.string().refine((s) => {
    try { const v: unknown = JSON.parse(s); return Array.isArray(v) && v.every((a) => typeof a === 'string'); } catch { return false; }
  }, 'JSON array of strings'),
  slot_hour: z.number().int().min(0).max(23).nullable(),
  client_url: HttpsUrlSchema.nullable(),
  offered: Bit.nullable(),
  active: Bit,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
}).strict();
export type SeoTreatmentRow = z.infer<typeof SeoTreatmentRowSchema>;

/** Fact keys are snake_case, e.g. dha_facility_licence, price_from_aed. */
export const FactKeySchema = z.string().min(2).max(64).regex(/^[a-z][a-z0-9_]*$/);
export const FactSourceSchema = z.string().max(80).regex(/^(approval|staff|import):[A-Za-z0-9_-]+$/);
export const SeoFactRowSchema = z.object({
  id: IdSchema,
  tenant_id: TenantIdSchema,
  treatment_id: IdSchema.nullable(),
  key: FactKeySchema,
  value_json: z.string().refine((s) => { try { JSON.parse(s); return true; } catch { return false; } }, 'valid JSON'),
  source: FactSourceSchema,
  confirmed_by: IdSchema.nullable(),
  confirmed_at: IsoDateTimeSchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
}).strict();
export type SeoFactRow = z.infer<typeof SeoFactRowSchema>;

/** Staff read views (GET /v1/seo-loop/admin/tenants/:tenantId/...). Contacts carry PII: staff-only, audited. */
export interface SeoSettingsView { tenantId: string; settings: SeoTenantSettingsRow | null }
export interface SeoListView<T> { tenantId: string; items: T[] }

// ------------------------------------------------------------------------------------------------ approval links (PR 2)
// Growth 0601. Flag seo-loop.approval_links (default off) AND seo-loop.enabled: both must be on.
// Staff build a request (items with EN + AR prompts) for one tenant; the client's single approver answers on the public
// page /a/:token (customer portal host, no login). Answers are stored only; they do not write seo_facts (PR 4).

export const SEO_LOOP_APPROVAL_LINKS_FLAG = 'seo-loop.approval_links' as const;
export const APPROVAL_LINK_DAYS_DEFAULT = 30;
export const APPROVAL_LINK_DAYS_MAX = 30;
export const APPROVAL_ITEMS_MAX = 150;
/** Largest AED amount accepted for a price answer (sanity bound, not a business rule). */
export const AED_MAX = 10_000_000;

export const ApprovalSectionSchema = z.enum(['clinic', 'site_fix', 'treatment', 'page', 'access']);
export type ApprovalSection = z.infer<typeof ApprovalSectionSchema>;
/** Order of sections on the client page. */
export const APPROVAL_SECTION_ORDER: readonly ApprovalSection[] = ['clinic', 'site_fix', 'treatment', 'page', 'access'];
export const ApprovalItemKindSchema = z.enum(['confirm', 'yes_no', 'text', 'number_aed', 'choice']);
export type ApprovalItemKind = z.infer<typeof ApprovalItemKindSchema>;
export const ApprovalRequestStatusSchema = z.enum(['open', 'partially_answered', 'completed', 'expired', 'revoked']);
export type ApprovalRequestStatus = z.infer<typeof ApprovalRequestStatusSchema>;

const Prompt = z.string().trim().min(1).max(500);
export const ApprovalOptionSchema = z.object({
  value: z.string().trim().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
  label_en: z.string().trim().min(1).max(120),
  label_ar: z.string().trim().min(1).max(120),
}).strict();
export type ApprovalOption = z.infer<typeof ApprovalOptionSchema>;

/** One item as staff send it (POST .../approval-requests). treatmentId must be one of the tenant's seo_treatments. */
export const ApprovalItemInputSchema = z.object({
  section: ApprovalSectionSchema,
  kind: ApprovalItemKindSchema,
  promptEn: Prompt,
  promptAr: Prompt,
  treatmentId: IdSchema.optional(),
  currentValue: z.string().trim().max(2000).optional(),
  options: z.array(ApprovalOptionSchema).min(2).max(20).optional(),
  factKey: FactKeySchema.optional(),
  required: z.boolean().default(true),
}).strict().superRefine((it, ctx) => {
  if (it.kind === 'choice' && !it.options) ctx.addIssue({ code: 'custom', path: ['options'], message: 'a choice item needs options' });
  if (it.kind !== 'choice' && it.options) ctx.addIssue({ code: 'custom', path: ['options'], message: 'options are for choice items only' });
  if (it.options && new Set(it.options.map((o) => o.value)).size !== it.options.length) ctx.addIssue({ code: 'custom', path: ['options'], message: 'option values must be unique' });
  if (it.section === 'treatment' && !it.treatmentId) ctx.addIssue({ code: 'custom', path: ['treatmentId'], message: 'a treatment item needs treatmentId' });
});
export type ApprovalItemInput = z.input<typeof ApprovalItemInputSchema>;

export const ApprovalRequestCreateSchema = z.object({
  title: z.string().trim().min(1).max(160),
  expiresInDays: z.number().int().min(1).max(APPROVAL_LINK_DAYS_MAX).default(APPROVAL_LINK_DAYS_DEFAULT),
  items: z.array(ApprovalItemInputSchema).min(1).max(APPROVAL_ITEMS_MAX),
}).strict();
export type ApprovalRequestCreate = z.input<typeof ApprovalRequestCreateSchema>;

/** Build a request from a repo fixture (today only the first tenant's checklist, docs/plans/fixtures/seo-loop-fiore-seed.json). */
export const ApprovalFixtureCreateSchema = z.object({
  fixture: z.enum(['fiore']),
  title: z.string().trim().min(1).max(160).optional(),
  expiresInDays: z.number().int().min(1).max(APPROVAL_LINK_DAYS_MAX).default(APPROVAL_LINK_DAYS_DEFAULT),
}).strict();

const Note = z.string().trim().max(1000).optional();
/**
 * The client's answer to one item (PATCH /v1/seo-loop/public/approvals/:token/items/:itemId, autosave).
 * Which answers an item accepts depends on its kind: see answerFitsKind.
 */
export const ApprovalAnswerInputSchema = z.discriminatedUnion('answer', [
  z.object({ answer: z.literal('confirmed'), note: Note }).strict(),
  z.object({ answer: z.literal('corrected'), value: z.string().trim().min(1).max(2000), note: Note }).strict(),
  z.object({ answer: z.literal('yes'), note: Note }).strict(),
  z.object({ answer: z.literal('no'), note: Note }).strict(),
  z.object({ answer: z.literal('text'), value: z.string().trim().min(1).max(2000), note: Note }).strict(),
  /** AED, non-negative, at most 2 decimals */
  z.object({ answer: z.literal('amount'), value: z.number().finite().min(0).max(AED_MAX).refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, 'at most 2 decimals'), note: Note }).strict(),
  /** "Do not show a price" on the website */
  z.object({ answer: z.literal('hide_price'), note: Note }).strict(),
  z.object({ answer: z.literal('choice'), value: z.string().trim().min(1).max(64), note: Note }).strict(),
]);
export type ApprovalAnswerInput = z.infer<typeof ApprovalAnswerInputSchema>;
export type ApprovalAnswerKind = ApprovalAnswerInput['answer'];

export const ANSWERS_BY_KIND: Record<ApprovalItemKind, readonly ApprovalAnswerKind[]> = {
  confirm: ['confirmed', 'corrected'],
  yes_no: ['yes', 'no'],
  text: ['text'],
  number_aed: ['amount', 'hide_price'],
  choice: ['choice'],
};
/** True when the answer is one the item's kind accepts (and, for a choice, one of its option values). */
export function answerFitsKind(kind: ApprovalItemKind, input: ApprovalAnswerInput, options: readonly ApprovalOption[] | null = null): boolean {
  if (!ANSWERS_BY_KIND[kind].includes(input.answer)) return false;
  if (input.answer === 'choice') return !!options?.some((o) => o.value === input.value);
  return true;
}

/** Client-safe answer (no ip hash, user agent or answer id). value is a string as stored (amount as decimal text). */
export interface PublicApprovalAnswer { answer: ApprovalAnswerKind; value: string | null; note: string | null; answeredAt: string }
/**
 * Client-safe item. Allow-listed fields only: no tenant id, fact key, staff names, internal notes or competitor data.
 * treatmentName is the tenant's own treatment name.
 */
export interface PublicApprovalItem {
  id: string; section: ApprovalSection; kind: ApprovalItemKind; treatmentName: string | null; promptEn: string; promptAr: string;
  currentValue: string | null; options: ApprovalOption[] | null; required: boolean; answer: PublicApprovalAnswer | null;
}
export interface PublicApprovalRequest {
  title: string; businessName: string; status: 'open' | 'partially_answered' | 'completed'; expiresAt: string; completedAt: string | null;
  progress: { answered: number; total: number; requiredMissing: number }; items: PublicApprovalItem[];
}
export const PUBLIC_APPROVAL_REQUEST_KEYS = ['title', 'businessName', 'status', 'expiresAt', 'completedAt', 'progress', 'items'] as const;
export const PUBLIC_APPROVAL_ITEM_KEYS = ['id', 'section', 'kind', 'treatmentName', 'promptEn', 'promptAr', 'currentValue', 'options', 'required', 'answer'] as const;

/** Staff views (admin surface). The raw token / URL is only in ApprovalRequestIssued (create and re-issue). */
export interface ApprovalRequestSummary {
  id: string; tenantId: string; title: string; status: ApprovalRequestStatus; expiresAt: string; createdBy: string; createdAt: string;
  completedAt: string | null; revokedAt: string | null; lastViewedAt: string | null; reissuedAt: string | null;
  progress: { answered: number; total: number; requiredMissing: number };
}
export interface StaffApprovalAnswer extends PublicApprovalAnswer { id: string; current: boolean; userAgent: string | null }
export interface StaffApprovalItem extends Omit<PublicApprovalItem, 'answer'> {
  treatmentId: string | null; factKey: string | null; sort: number; answer: PublicApprovalAnswer | null; history: StaffApprovalAnswer[];
}
export interface ApprovalRequestDetail extends ApprovalRequestSummary { items: StaffApprovalItem[] }
export interface ApprovalRequestIssued { request: ApprovalRequestSummary; url: string }
